require('./style.scss');
import { jobKey, buildableRooms, planForService, startBuild, getStatus, fileUrl, hasWorlds, marbleUrl } from './api';
import ModelPreview from './preview';

const POLL_MS = 4000;
const FILL_VIEWS = 6;   // utils/convert3d/complete.py POSITIONS: at most one paint per eye position

// Room quality from the depth-vs-drawn wall check (utils/convert3d geometry.wall_check).
export function roomQuality(walls) {
  if (!walls) return { level: 'unknown', text: 'no wall check' };
  const cm = Math.round(walls.median_abs_mm / 10);
  const within = Math.round(walls.within_100mm * 100);
  const level = walls.median_abs_mm <= 150 && walls.within_100mm >= 0.3 ? 'good'
    : walls.median_abs_mm <= 400 ? 'fair' : 'poor';
  return { level, text: `walls ±${cm} cm · ${within}% within 10 cm` };
}

export { hasWorlds, marbleUrl };

export function statusText(s) {
  switch (s && s.state) {
    case 'queued': return 'Waiting for the 3D service…';
    case 'running': return s.current && /^AI /.test(s.current) ? `${s.current}…` : s.total ? `Building room ${Math.min(s.done + 1, s.total)} of ${s.total}${s.current ? ` (${s.current})` : ''}… about 3 min per room`
      : 'Building…';
    case 'done': return `Built ${new Date(s.updated * 1000).toLocaleString()}`;
    case 'error': return `Build failed: ${s.error || 'unknown error'}`;
    default: return 'Not built yet.';
  }
}

export function worldsText(s) {
  const rooms = (s && s.report && s.report.rooms) || [];
  const made = rooms.filter(r => r.world && r.world.spz).length;
  return made ? `AI worlds for ${made} of ${rooms.length} room(s).` : 'Not generated yet.';
}

/**
 * "3D model" panel in the admin floor editor (prototype, branch feat/convert3d): builds a textured 3D model of
 * the tour from the floor plan as drawn (camera positions, lens height, rooms) + MoGe-2 depth on each panorama,
 * then previews it with a per-room check against the drawn walls. A second tab, "3D Marble", generates World Labs
 * AI worlds per room. Both run as the one build the service keeps per tour, so a running build locks both tabs.
 *
 *   new Convert3dPanel({ container, getPlan, onClose }).open()
 */
export default class Convert3dPanel {
  constructor({ container, getPlan, onClose, api = { startBuild, getStatus, fileUrl },
    Preview = ModelPreview, hostname = window.location.hostname }) {
    this._container = container;
    this._getPlan = getPlan;
    this._key = jobKey(getPlan(), hostname);
    this._onClose = onClose || (() => {});
    this._api = api;
    this._Preview = Preview;
    this._status = null;
    this._shownBuild = null;
    this._closed = false;
  }

  open() {
    const el = document.createElement('div');
    el.className = 'e3-convert3d';
    el.innerHTML = `
      <div class="e3-convert3d__head"><b>3D</b> <span class="e3-convert3d__beta">beta</span>
        <button type="button" class="e3-convert3d__x" title="Close">×</button></div>
      <div class="e3-convert3d__tabs">
        <button type="button" data-tab="model" class="is-active" title="Our model: built from the panoramas and the floor plan as drawn">Our 3D</button>
        <button type="button" data-tab="marble" title="AI worlds per room made by World Labs (Marble)">3D Marble</button></div>
      <div class="e3-convert3d__pane" data-pane="model">
        <div class="e3-convert3d__status"></div>
        <div class="e3-convert3d__actions"><button type="button" class="e3-convert3d__build">Build 3D</button></div>
        <label class="e3-convert3d__merge" title="The rooms are parts of one space (e.g. three photos of one kitchen): no walls between them, each part shown from the nearest photo">
          <input type="checkbox"> Open plan: merge rooms into one space</label>
        <label class="e3-convert3d__merge e3-convert3d__fill" title="Paint with AI (OpenAI) what walking about 1.5 m around each capture point would show missing, like 3D Marble invents unseen parts">
          <input type="checkbox"> AI fill: complete hidden areas (OpenAI)</label>
        <div class="e3-convert3d__view" hidden></div>
        <div class="e3-convert3d__rooms"></div>
        <div class="e3-convert3d__note">Uses the floor plan as drawn now: room positions, lens height, ceilings.
          "Check" shows the room as drawn (red) and the walls the depth model sees (blue).</div></div>
      <div class="e3-convert3d__pane" data-pane="marble" hidden>
        <div class="e3-convert3d__status e3-convert3d__marble-status"></div>
        <div class="e3-convert3d__actions">
          <select class="e3-convert3d__quality" title="AI world quality: Plus is World Labs' larger model (more detail, more credits)">
            <option value="standard">Standard</option><option value="plus">Plus</option></select>
          <button type="button" class="e3-convert3d__marble" title="Generate an AI 3D world per room with World Labs (Marble)">Generate worlds</button>
          <button type="button" class="e3-convert3d__open-marble" hidden>Open 3D Marble</button></div>
        <div class="e3-convert3d__worlds"></div>
        <div class="e3-convert3d__note">One AI world per room, walkable anywhere; parts the camera did not see are
          invented. Uses World Labs credits (about $1 per room, Plus more); rooms already generated for the same photo
          are reused at no cost.</div></div>`;
    this._el = el;
    this._statusEl = el.querySelector('.e3-convert3d__status');
    this._marbleStatusEl = el.querySelector('.e3-convert3d__marble-status');
    this._buildBtn = el.querySelector('.e3-convert3d__build');
    this._viewEl = el.querySelector('.e3-convert3d__view');
    this._roomsEl = el.querySelector('.e3-convert3d__rooms');
    this._worldsEl = el.querySelector('.e3-convert3d__worlds');
    el.querySelector('.e3-convert3d__x').addEventListener('click', () => this.close());
    el.querySelectorAll('.e3-convert3d__tabs button').forEach(b => b.addEventListener('click', () => this._tab(b.dataset.tab)));
    this._buildBtn.addEventListener('click', () => {
      if (this._busy) return;
      const rooms = buildableRooms(this._getPlan()).length;
      if (this._fillEl.checked && !window.confirm(`Build with AI fill for ${rooms} room(s)?\n\n`
        + `Each room gets up to ${FILL_VIEWS} OpenAI image edits (views around the capture point that show holes) and `
        + 'about 2 more minutes. Painted parts are invented by the AI; views painted before are reused at no cost.')) return;
      this._build();
    });
    this._marbleBtn = el.querySelector('.e3-convert3d__marble');
    this._openMarbleBtn = el.querySelector('.e3-convert3d__open-marble');
    this._qualityEl = el.querySelector('.e3-convert3d__quality');
    this._mergeEl = el.querySelector('.e3-convert3d__merge input');
    this._fillEl = el.querySelector('.e3-convert3d__fill input');
    this._mergeEl.addEventListener('change', () => { this._mergeTouched = true; });
    this._fillEl.addEventListener('change', () => { this._fillTouched = true; });
    this._marbleBtn.addEventListener('click', () => {
      if (this._busy) return;
      const rooms = buildableRooms(this._getPlan()).length;
      const plus = this._qualityEl.value === 'plus';
      if (window.confirm(`Generate an AI 3D world for each of the ${rooms} room(s) with World Labs${plus ? ' (Plus quality)' : ''}?\n\n`
        + `Takes about 6 minutes and uses World Labs credits (${plus ? 'Plus costs more than the roughly $1 per room of Standard' : 'roughly $1 per room'}). `
        + 'Parts the camera did not see are invented by the AI. Rooms already generated for the same photo are reused at no cost.')) {
        this._build(true, plus ? 'plus' : 'standard');
      }
    });
    this._openMarbleBtn.addEventListener('click', () => this.openMarble());
    ['mousedown', 'mouseup', 'click', 'wheel', 'touchstart', 'pointerdown'].forEach(type =>
      el.addEventListener(type, e => e.stopPropagation()));   // keep clicks away from the panorama / floor editor
    this._container.appendChild(el);
    if (!this._key) return this._show({ state: 'error', error: 'draw at least one room first' });
    this._lock(true);   // until the service says whether a build is already running for this tour
    this._statusEl.textContent = this._marbleStatusEl.textContent = 'Checking…';
    this._refresh();
    return this;
  }

  _tab(name) {
    this._el.querySelectorAll('.e3-convert3d__tabs button').forEach(b => b.classList.toggle('is-active', b.dataset.tab === name));
    this._el.querySelectorAll('.e3-convert3d__pane').forEach(p => { p.hidden = p.dataset.pane !== name; });
  }

  // Both buttons start the one job the service runs per tour (a Marble build also rebuilds our model), so while
  // either runs, neither can start another.
  _lock(busy) {
    this._busy = busy;
    [this._buildBtn, this._marbleBtn, this._qualityEl, this._mergeEl, this._fillEl].forEach(b => {
      if (b.dataset.title === undefined) b.dataset.title = b.title;
      b.disabled = busy;
      b.title = busy ? 'A 3D build is running for this tour, wait until it finishes' : b.dataset.title;
    });
  }

  close() {
    if (this._closed) return;
    this.closeMarble();
    this._closed = true;
    clearTimeout(this._timer);
    if (this._preview) this._preview.destroy();
    if (this._el) this._el.remove();
    this._onClose();
  }

  async _build(worlds = false, quality = 'standard') {
    const plan = this._getPlan();
    const rooms = buildableRooms(plan);
    if (this._busy) return;
    if (!rooms.length) return this._show({ state: 'error', error: 'draw at least one room first' });
    this._lock(true);
    this._kind = worlds ? 'worlds' : 'model';
    const payload = planForService(plan);
    const merge = this._mergeEl.checked;
    const complete = !worlds && this._fillEl.checked;
    if (worlds || merge || complete) {
      payload._convert3d = { ...(worlds && { worlds: true, quality }), ...(merge && { merge: true }),
        ...(complete && { complete: true }) };   // server.py job_args
    }
    try {
      this._show(await this._api.startBuild(this._key, payload));
      this._poll();
    } catch (e) {
      this._show({ state: 'error', error: e.message });
    }
  }

  async _refresh() {
    try {
      this._show(await this._api.getStatus(this._key));
      this._poll();
    } catch (e) {
      this._show({ state: 'error', error: e.message });
    }
  }

  _poll() {
    clearTimeout(this._timer);
    const s = this._status;
    if (!this._closed && s && (s.state === 'queued' || s.state === 'running')) {
      this._timer = setTimeout(() => this._refresh(), POLL_MS);
    }
  }

  _show(s) {
    if (this._closed) return;
    this._status = s;
    const busy = s.state === 'queued' || s.state === 'running';
    // which button's build runs: the service says (kind); older service → what this panel started / the AI phase
    const kind = busy ? s.kind || this._kind || (/^AI /.test(s.current || '') ? 'worlds' : 'model') : null;
    if (busy && !this._kind) this._tab(kind === 'worlds' ? 'marble' : 'model');   // opened while a build runs
    this._kind = kind;
    this._lock(busy);
    this._statusEl.textContent = kind === 'worlds' ? '3D Marble is generating, the 3D model can be rebuilt when it finishes.'
      : statusText(s);
    this._statusEl.dataset.state = kind === 'worlds' ? 'running' : s.state || 'none';
    this._marbleStatusEl.textContent = kind === 'model' ? 'Our 3D model is building, worlds can be generated when it finishes.'
      : busy || s.state === 'error' ? statusText(s) : worldsText(s);
    this._marbleStatusEl.dataset.state = busy ? 'running' : s.state === 'error' ? 'error' : hasWorlds(s) ? 'done' : 'none';
    this._buildBtn.textContent = s.state === 'done' || s.report ? 'Rebuild' : 'Build 3D';
    this._openMarbleBtn.hidden = !hasWorlds(s);
    if (s.report && !this._mergeTouched) this._mergeEl.checked = (s.report.rooms || []).some(r => r.merged);   // as last built
    if (s.report && !this._fillTouched) this._fillEl.checked = (s.report.rooms || []).some(r => r.fill);
    if (s.state === 'done' && s.report && this._shownBuild !== s.updated) {
      this._shownBuild = s.updated;
      this._showResult(s);
    }
  }

  // the AI worlds in their own page (data/convert3d/world.html: three r186 + Spark), in a layer over the tour
  openMarble() {
    if (this._marble) return;
    const layer = document.createElement('div');
    layer.className = 'e3-convert3d-marble';
    const frame = document.createElement('iframe');
    frame.src = marbleUrl(this._key);
    frame.allow = 'fullscreen';
    frame.title = '3D Marble';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'e3-convert3d-marble__close';
    close.title = 'Close';
    close.textContent = '×';
    close.addEventListener('click', () => this.closeMarble());
    layer.append(frame, close);
    ['mousedown', 'mouseup', 'click', 'wheel', 'touchstart', 'pointerdown', 'keydown']
      .forEach(type => layer.addEventListener(type, e => e.stopPropagation()));
    this._container.appendChild(layer);
    this._marble = layer;
  }

  closeMarble() {
    if (this._marble) this._marble.remove();
    this._marble = null;
  }

  _showResult(s) {
    const version = Math.round(s.updated || 0);
    this._viewEl.hidden = false;
    if (!this._preview) this._preview = new this._Preview(this._viewEl);
    this._preview.load(this._api.fileUrl(this._key, 'model.glb', version), (s.report.rooms || []).filter(r => !r.error))
      .catch(() => { this._statusEl.textContent = 'Built, but the 3D file could not be loaded.'; });
    this._roomsEl.textContent = '';
    (s.report.rooms || []).forEach(room => {
      const row = document.createElement('div');
      row.className = 'e3-convert3d__room';
      const name = document.createElement('span');
      name.className = 'e3-convert3d__name';
      name.textContent = room.name || room.scene;
      const info = document.createElement('span');
      info.className = 'e3-convert3d__info';
      if (room.error) {
        info.textContent = 'failed';
        info.title = room.error;
        info.dataset.level = 'poor';
      } else {
        const q = roomQuality(room.walls);
        info.textContent = q.text + (room.fill ? ` · AI fill ${room.fill.painted}` : '');
        info.dataset.level = q.level;
        if (room.fill) {
          info.title = `AI fill: ${room.fill.painted} view(s) painted, holes walking around `
            + `${(room.fill.holes_before * 100).toFixed(1)}% → ${(room.fill.holes_after * 100).toFixed(1)}%`;
        }
      }
      row.append(name, info);
      if (!room.error) {
        const view = document.createElement('button');
        view.type = 'button';
        view.textContent = 'View';
        view.title = 'Stand where this panorama was taken';
        view.addEventListener('click', () => this._preview && this._preview.goTo(room.scene));
        const check = document.createElement('a');
        check.textContent = 'Check';
        check.target = '_blank';
        check.rel = 'noopener';
        check.href = this._api.fileUrl(this._key, `rooms/${room.scene}-check.jpg`, version);
        row.append(view, check);
      }
      this._roomsEl.appendChild(row);
    });
    this._showWorlds(s.report.rooms || []);
  }

  _showWorlds(rooms) {
    this._worldsEl.textContent = '';
    rooms.forEach(room => {
      const row = document.createElement('div');
      row.className = 'e3-convert3d__room';
      const name = document.createElement('span');
      name.className = 'e3-convert3d__name';
      name.textContent = room.name || room.scene;
      const info = document.createElement('span');
      info.className = 'e3-convert3d__info';
      const world = room.world || {};
      if (world.spz) {
        info.textContent = /plus/.test(world.model || '') ? 'world ready · Plus' : 'world ready';
        info.dataset.level = 'good';
      } else if (world.error) {
        info.textContent = 'failed';
        info.title = world.error;
        info.dataset.level = 'poor';
      } else {
        info.textContent = 'not generated';
        info.dataset.level = 'unknown';
      }
      row.append(name, info);
      this._worldsEl.appendChild(row);
    });
  }
}
